import React, { useEffect } from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {FormInput} from '../../../../../../components/shared/form-controls/form-input';
import {FormCheckbox} from '../../../../../../components/shared/form-controls/form-checkbox';
import {useAppDispatch, useAppSelector} from '../../../../../../hooks/hooks';
import {addTagType, filterTagType} from '../../../../../../features/types';
import {TiminatorTagType} from '../../../../../../features/types/types';
import { useIntl } from 'react-intl';
import { Lx } from '../../../../../../i18n/consts';
import BaseForm from '../../../../../../components/shared/form-controls/base-form';
import { useNavigate } from 'react-router-dom';
import { NAVIGATION_ROUTES } from '../../../../../../routes/NavigationRoute';
import { canManageTagTypes } from '../../../../../../features/profile/entityPermissions';

interface CreateForm {
    name: string;
    isDefault: boolean;
    isActive: boolean;
}

export default function TagTypeCreateForm() {
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const navigate = useNavigate();
    const profile = useAppSelector(store => store.profile);
    const canEditTagTypes = canManageTagTypes(profile);

    useEffect(() => {
        if (!canEditTagTypes) {
            navigate(NAVIGATION_ROUTES.TAG_TYPES);
        }
    }, [canEditTagTypes, navigate]);

    if (!canEditTagTypes) {
        return null;
    }

    const initialValue: CreateForm = {
        name: '',
        isDefault: false,
        isActive: true,
    };

    const validateName = async (name: string): Promise<boolean> => {
        name = name.toLowerCase().trim();
        if (name === '') {
            return Promise.resolve(false);
        }
        return await dispatch(filterTagType(name)).unwrap().then((values) => {
            return values.length < 1 || values.some(value => value.name.toLowerCase() !== name);
        });
    };

    const validationScheme: Yup.Schema<CreateForm> = Yup.object({
        name: Yup.string()
            .max(45, 'Must be 45 characters or less')
            .required(intl.formatMessage({ id: Lx.Form.NAME_REQUIRED_FORMAT }, { s: intl.formatMessage({ id: Lx.General.TAG_TYPE }) }))
            .test(
                'type-name-backend-validation',
                'tag name is taken',
                async (name) => await validateName(name)
            ),
        isDefault: Yup.boolean().required(),
        isActive: Yup.boolean().required(),
    });
    
    const save = async (values: CreateForm) => {
        const type: TiminatorTagType = {
            id: 0,
            name: values.name,
            active: values.isActive,
            default: values.isDefault
        };

        return dispatch(addTagType(type));
    };

    return <Formik
        initialValues={initialValue}
        validationSchema={validationScheme}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={save}>
        <Form>
            <BaseForm contentGap={24}
                formType='create'
                formHeader='create_tag_type'
                savingTitle='Tag type' 
                onClose={() => navigate(NAVIGATION_ROUTES.TAG_TYPES)} 
                onAfterSave={() => navigate(NAVIGATION_ROUTES.TAG_TYPES)}>
                <FormInput name="name" label="Name" placeholder="Tag" />
                <fieldset>
                    <FormCheckbox name="isDefault" label="Default" />
                    <FormCheckbox name="isActive" label="Active" />
                </fieldset>
            </BaseForm>
        </Form>
    </Formik>
}
